// Percorso file: src/utils/squadre.js
//
// LA SQUADRA E L'ANNATA — sede unica della lettura di squadra nel browser.
//
// ⚠️ NIENTE QUI DENTRO È NUOVO. Ogni corpo di questo file è la COPIA VERBATIM
// di una funzione che viveva — identica, e spesso in quattro o sette esemplari
// — dentro le pagine. Il cantiere «lettore unico» non ha riscritto nulla: ha
// tolto le copie e le ha portate qui. Chi modifica una regola la modifica in
// un posto solo; chi ne vuole una diversa aggiunge un PARAMETRO e lo dichiara,
// perché le differenze fra le pagine sono VOLUTE e ognuna è a verbale.
//
// ⚠️ MODULO PURO, E DEVE RESTARLO: nessun React, nessun hook, nessun import di
// `AuthContext`. Si importa `auth/access` (il gate unico dei ruoli) e
// `utils/stagione` (il metro dell'anno), e basta. `utils/roleUtils` RI-ESPORTA
// da qui la sentinella: importarlo di ritorno aprirebbe un ciclo.
//
// ⚠️ IL MODELLO. La squadra NON sta sulla persona (`user.team` è il campo
// morto): sta sulla TESSERA, e una persona ha una tessera per annata. La
// tessera porta la coppia canonica `{ teams, allTeams }` — l'elenco dei nomi
// e il permesso «tutte le squadre», che non è un nome.

// `normalizza` è `normalizeRole` (roleUtils.js lo ri-esporta con quel nome):
// stesso simbolo, stesso comportamento — String(v ?? "").trim().toUpperCase().
// Si prende dalla sede unica invece che da `roleUtils` per non chiudere il
// ciclo di import descritto qui sopra.
import { canonicalRole, normalizeRole as normalizza } from "../auth/access";
import { annoDiLista } from "./stagione";

/* =========================================================================
   LA SENTINELLA DI `approvedTeams`
   ========================================================================= */

/**
 * Sentinella di `approvedTeams`: NON è una squadra, vuol dire «tutte le
 * squadre». La legge Messaging per aprire le chat di ogni squadra.
 *
 * Chi la SCRIVE (il commento che viveva in TeamAssignment diceva «la pagina
 * non la scrive», ed era falso: si è corretto invece di tramandarlo).
 * Due scrittori vivi, ed è bene conoscerli entrambi perché si comportano
 * in modo diverso:
 *   - TeamAssignment, quando l'amministratore spunta «All teams» e preme
 *     Save (`[TUTTE_LE_SQUADRE]`, revisione del 6/8/2026 — o la sentinella
 *     da sola, o l'elenco esplicito senza sentinella, mai i due insieme).
 *     Qui la sentinella è CHIESTA da chi amministra;
 *   - UserContextManager, il cui Save riscrive il context COACH con
 *     `approvedTeams: teamTD ? ["ALL"] : []`. Qui NON è chiesta: una lista
 *     parziale (`["U12"]`) viene ALLARGATA a tutte le squadre senza che
 *     nessuno l'abbia domandato — reperto già a verbale in quella pagina,
 *     materia della forma del suo payload, non di questo modulo.
 * Nei DATI la sentinella può esserci anche per eredità storica.
 * UsersList la legge soltanto: non scrive nulla.
 *
 * ⚠️ SEDE UNICA (P7, poi il cantiere «lettore unico»). Viveva in
 * `pages/TeamAssignment.jsx`, poi in `utils/roleUtils.js`. Sta ora qui,
 * accanto alla regola che è l'unica a leggerla — il ripiego di
 * `squadreDellaTessera` — perché `approvedTeams` non è un fatto di RUOLI:
 * è un campo di tessera, e ciò che resta vivo di lui è la lettura di squadra.
 * `roleUtils.js` la RI-ESPORTA, così i suoi importatori storici non cambiano
 * strada e non nasce la copia locale.
 * La costante omonima del backend (server.js) è un altro processo, non una
 * seconda sede di questo modulo.
 */
export const TUTTE_LE_SQUADRE = "ALL";

/** Vero se l'elenco delle squadre approvate porta la sentinella. */
export function valeSuTutteLeSquadre(approvedTeams) {
  return (approvedTeams || []).some(
    (voce) => normalizza(voce) === TUTTE_LE_SQUADRE
  );
}

/* =========================================================================
   LE SQUADRE DI UNA TESSERA — la «regola B»
   ========================================================================= */

/**
 * Le squadre di UNA TESSERA, nella forma del modello B: `{ teams, allTeams }`.
 *
 * ⚠️ La coppia si guarda INSIEME, mai un campo alla volta: un `teams` senza
 * `allTeams` non è una forma nuova a metà, è una forma incoerente, e
 * mescolare un campo salvato con uno ricavato produrrebbe proprio
 * l'accoppiata che il modello vieta (allTeams vero con l'elenco pieno).
 * È la stessa regola — e lo stesso ordine dei rami — di `squadreDellaTessera`
 * nel backend: due letture dello stesso dato devono dire la stessa cosa.
 *
 * ⚠️ IL RIPIEGO SUI VECCHI CAMPI SERVE DAVVERO. `GET /users` proietta già la
 * coppia per ogni tessera (F2), ma `GET /users/:id` e `GET /users?uid=`
 * restituiscono i `contexts` COSÌ COME STANNO SUL DOCUMENTO
 * (server.js): non passano da `normalizeContexts`, quindi su quelle risposte la
 * coppia canonica può non esserci su una tessera non ancora riscritta.
 *
 * ⚠️ NIENTE FILTRO DEI NOMI VUOTI NEL RAMO CANONICO, ed è un vincolo, non una
 * dimenticanza: le quattro copie che questo corpo sostituisce filtravano le
 * voci vuote SOLO nel ripiego, e i loro chiamanti le filtrano di nuovo in
 * aggregazione. Aggiungerlo qui cambierebbe l'esito del ramo canonico IN
 * SILENZIO. Il filtro resta dov'è: nei chiamanti.
 *
 * Corpo: quello delle QUATTRO copie identiche di `MemberProfileModal`,
 * `TeamAssignment`, `HomeParent` e `HomePlayer`, invariato.
 */
export function squadreDellaTessera(ctx) {
  if (!ctx) return { teams: [], allTeams: false };

  if (Array.isArray(ctx.teams) && typeof ctx.allTeams === "boolean") {
    // allTeams vero impone l'elenco vuoto, anche se qualcuno l'avesse scritto.
    return { teams: ctx.allTeams ? [] : ctx.teams, allTeams: ctx.allTeams };
  }

  const approvate = Array.isArray(ctx.approvedTeams) ? ctx.approvedTeams : [];

  if (valeSuTutteLeSquadre(approvate)) return { teams: [], allTeams: true };

  // Le voci vuote non sono nomi di squadra: non entrano nell'elenco.
  return {
    teams: approvate.filter((voce) => normalizza(voce) !== ""),
    allTeams: false
  };
}

/**
 * La tessera AMMINISTRATIVA: quella il cui ruolo è ADMIN o SUPERADMIN, in
 * forma canonica.
 *
 * ⚠️ NON PUÒ POGGIARE SULL'ANNO, ed è un fatto del dato: le cariche
 * amministrative sono PROGRESSIVE, non annuali, e non portano `cardSeason`
 * (AuthContext, cancello dell'anno). Filtrarle per annata vorrebbe dire farle
 * sparire tutte insieme dalle pagine che le governano.
 *
 * Corpo: quello delle DUE copie identiche di `UsersList` e
 * `MemberProfileModal`, invariato.
 */
export const eTesseraAmministrativa = (ctx) => {
  const ruolo = canonicalRole(ctx?.role);
  return ruolo === "ADMIN" || ruolo === "SUPERADMIN";
};

/* =========================================================================
   IL PREDICATO «TESSERA + ANNATA» — un corpo, due assi di parametri
   ========================================================================= */

/**
 * «Questa TESSERA vale per l'anno chiesto?»
 *
 * ⚠️ DUE PARAMETRI, NON UNO, e nessuno dei due è un lusso: le sei sedi che
 * questo corpo sostituisce si distinguono su DUE assi indipendenti, e ogni
 * differenza è dichiarata a verbale dalla pagina che la porta.
 *
 *   `stati`      quali `status` sono ammessi. Default `["ACTIVE"]` (User List,
 *                la scheda socio, le due home, UserContextManager).
 *                `null` = QUALUNQUE stato: è TeamAssignment (differenza D1),
 *                «la pagina in cui si APPROVA», dove PENDING e ARCHIVED devono
 *                comparire o l'amministratore non può lavorarli.
 *                `["ACTIVE","PENDING"]` è la finestra dei ruoli di
 *                UserContextManager (differenza D10).
 *
 *   `senzaAnno`  che fare della tessera che l'anno non ce l'ha.
 *                `"escludi"` (default) — senza anno non è «di quest'anno»:
 *                resta fuori. `"includi"` è di nuovo la finestra dei ruoli di
 *                UserContextManager (D10), che le tessere MUTE le governa
 *                perché è esattamente l'insieme che la sua rotta tocca.
 *
 * ⚠️ PRESENZA DELLA CHIAVE `cardSeason`, non confronto a `null`: il campo
 * viaggia con uno spread condizionale (server.js), quindi una tessera che non
 * ce l'ha arriva SENZA LA CHIAVE, e `ctx.cardSeason === null` non sarebbe mai
 * vero. Nel ramo `"includi"` la domanda è un'altra — «l'anno è leggibile?» —
 * e si pone sul VALORE, come faceva la closure che quel ramo sostituisce.
 *
 * ⚠️ ANNATA IGNOTA (`anno === null`) → la regola dell'anno NON si applica.
 * È la dottrina comune a tutte e sei le copie: nei millisecondi
 * dell'apertura, o dopo una lettura della stagione fallita, su un dato che
 * non si sa leggere non si inventa nulla e non si fa sparire nessuno.
 *
 * ⚠️ STATUS NORMALIZZATO, non confronto crudo: una tessera storica salvata
 * `status: "Active"` conta. Nessun default: status assente non è ACTIVE.
 */
export const tesseraDellAnno = (ctx, anno, opzioni = {}) => {
  const { stati = ["ACTIVE"], senzaAnno = "escludi" } = opzioni;

  if (stati !== null) {
    const stato = normalizza(ctx?.status);
    if (!stati.some((ammesso) => normalizza(ammesso) === stato)) return false;
  }

  if (anno === null) return true;

  if (senzaAnno === "includi") {
    // L'anno LEGGIBILE, non la presenza della chiave: una tessera muta —
    // chiave assente, oppure valore che anno non è — passa.
    const annoTessera = annoDiLista(ctx?.cardSeason);
    return annoTessera === null || annoTessera === anno;
  }

  // PRESENZA della chiave, non confronto a `null`: vedi sopra.
  if (!Object.prototype.hasOwnProperty.call(ctx || {}, "cardSeason")) {
    return false;
  }

  return annoDiLista(ctx.cardSeason) === anno;
};

/**
 * LE TESSERE DI UNA PERSONA che valgono per l'anno chiesto.
 *
 * `opzioni` = quelle di `tesseraDellAnno`, più:
 *   `includiAmministrative`  default `false`. Con `true` aggiunge in coda le
 *                            tessere AMMINISTRATIVE attive non già incluse.
 *
 * ⚠️ SONO DUE INSIEMI DIVERSI, DI PROPOSITO (differenza D3), e il default è
 * `false` per una ragione precisa:
 *   - senza le amministrative → le SQUADRE (colonna Team, tendina, filtro,
 *     ordinamento, le due home). Una tessera amministrativa NON è una squadra,
 *     e includerla scriverebbe «Admin: no team» accanto al nome di ogni
 *     amministratore del club;
 *   - con le amministrative → i RUOLI e le CARICHE (colonna Role, card che
 *     contano per ruolo, la sezione «Active Roles» della scheda socio). Lì il
 *     cappello admin c'è SEMPRE, anche nell'annata in cui la persona non ha
 *     altre tessere.
 *
 * ⚠️ L'ECCEZIONE NON ALLARGA LE ALTRE TESSERE DELL'ADMIN. Un ADMIN che è
 * anche Coach 2026, guardato nel 2027, ha qui la sola tessera amministrativa.
 *
 * ⚠️ L'ORDINE È PARTE DEL CONTRATTO: prima le valide, poi le amministrative.
 * È l'ordine delle due copie che questo corpo sostituisce, e la scheda socio
 * lo rende a schermo.
 */
export const tessereDelSoggetto = (contexts, anno, opzioni = {}) => {
  const {
    stati = ["ACTIVE"],
    senzaAnno = "escludi",
    includiAmministrative = false
  } = opzioni;

  const tessere = Array.isArray(contexts) ? contexts : [];

  const valide = tessere.filter(
    (c) => c && tesseraDellAnno(c, anno, { stati, senzaAnno })
  );

  if (!includiAmministrative) return valide;

  const amministrative = tessere.filter(
    (c) =>
      c &&
      normalizza(c.status) === "ACTIVE" &&
      eTesseraAmministrativa(c) &&
      !valide.includes(c)
  );

  return valide.concat(amministrative);
};

/* =========================================================================
   L'UNIONE DELLE SQUADRE
   ========================================================================= */

/**
 * L'UNIONE DELLE SQUADRE DI UN INSIEME DI TESSERE GIÀ SCELTE.
 *
 * ⚠️ ESISTE PER NON FILTRARE DUE VOLTE. La scheda socio calcola le sue
 * tessere operative in un `useMemo` e ne consuma l'esito in un secondo
 * `useMemo` concatenato: se anche le squadre ripartissero dai `contexts`, il
 * filtro girerebbe due volte e — peggio — l'insieme reso a schermo perderebbe
 * l'identità referenziale con quello da cui le squadre sono state ricavate.
 * Qui l'unione si fa, e basta.
 *
 * ⚠️ «TUTTE LE SQUADRE» NON SI SOMMA, ASSORBE: `allTeams` di una qualunque
 * tessera vince su tutto, perché è un PERMESSO e non un nome.
 *
 * `opzioni` — le due differenze VOLUTE fra le copie che questo corpo
 * sostituisce. Nessuna delle due è un'opinione: sono due esiti a schermo
 * diversi, e uniformarli cambierebbe una pagina.
 *
 *   `assorbeTutte`  default `true`: con `allTeams` l'elenco dei nomi esce
 *                   VUOTO. È il corpo della scheda socio e delle due home,
 *                   che il permesso poi lo rendono a parole («All teams» al
 *                   posto della squadra).
 *                   `false` conserva i nomi delle ALTRE tessere accanto al
 *                   permesso. È il corpo di `squadreDellaPersona` in User
 *                   List, e serve alla TENDINA di filtro: là i nomi si
 *                   raccolgono da tutte le persone, e svuotare l'elenco di
 *                   un coach «tutte le squadre» che è anche giocatore
 *                   dell'U12 toglierebbe «U12» dalla tendina.
 *
 *   `doppioni`      `"esatti"` (default) confronta i nomi già ripuliti così
 *                   come sono — è il corpo della scheda socio e delle due
 *                   home. `"normalizzati"` toglie anche «U12» contro «u12»,
 *                   tenendo la GRAFIA DELLA PRIMA occorrenza: è il corpo di
 *                   User List, che quella regola la dichiara nella tendina
 *                   («U12» e «u12» sono una voce sola, non due).
 *
 * ⚠️ IL FILTRO DELLE VOCI VUOTE STA QUI, in aggregazione, e non dentro
 * `squadreDellaTessera`: vedi l'avvertenza di quella funzione.
 */
export const squadreDelleTessere = (tessere, opzioni = {}) => {
  const { assorbeTutte = true, doppioni = "esatti" } = opzioni;

  const nomi = [];
  let tutte = false;

  (Array.isArray(tessere) ? tessere : []).forEach((ctx) => {
    const { teams, allTeams } = squadreDellaTessera(ctx);
    if (allTeams) tutte = true;

    // Con `allTeams` vero `teams` è vuoto per costruzione: il ciclo qui sotto
    // non aggiunge nulla, ed è il motivo per cui il ramo non ha bisogno di
    // un'uscita anticipata.
    teams.forEach((nome) => {
      if (doppioni === "normalizzati") {
        const chiave = normalizza(nome);
        if (chiave === "") return;
        if (!nomi.some((gia) => normalizza(gia) === chiave)) {
          nomi.push(String(nome).trim());
        }
        return;
      }

      const pulito = String(nome || "").trim();
      if (pulito && !nomi.includes(pulito)) nomi.push(pulito);
    });
  });

  return assorbeTutte && tutte ? { nomi: [], tutte: true } : { nomi, tutte };
};

/**
 * LE SQUADRE DI UNA PERSONA nell'anno chiesto, nella coppia `{ nomi, tutte }`.
 *
 * È il filtro delle tessere (`tessereDelSoggetto`) seguito dall'unione
 * (`squadreDelleTessere`): le opzioni sono quelle dei due, più
 *
 *   `ruoli`  default `null` = nessun filtro di ruolo. Con un elenco tiene le
 *            sole tessere il cui ruolo CANONICO vi compare.
 *
 * ⚠️ IL FILTRO DI RUOLO NON HA DEFAULT DIVERSO DA `null`, ed è la differenza
 * D6: la home dell'allenatore e i calendari chiedono «quali squadre ALLENA»,
 * cioè le sole tessere COACH — una persona Coach dell'U10 e Player nell'U18
 * non deve trovarsi l'U18 fra le squadre che allena. Ovunque altro la domanda
 * è «in quali squadre STA», e il ruolo non si guarda.
 *
 * ⚠️ IL FILTRO PER RUOLO DELLA COLONNA TEAM DI USER LIST NON PASSA DA QUI
 * (differenza D5): là si escludono le tessere PARENT, ma solo nella RESA
 * della colonna, mentre il filtro, la tendina e l'ordinamento della stessa
 * pagina leggono TUTTE le tessere valide. Sono due letture diverse di
 * proposito, e l'esclusione resta nel call site.
 */
export const squadreDelSoggetto = (contexts, anno, opzioni = {}) => {
  const { ruoli = null, assorbeTutte, doppioni } = opzioni;

  const tessere = tessereDelSoggetto(contexts, anno, opzioni);

  const scelte =
    ruoli === null
      ? tessere
      : tessere.filter((c) =>
          ruoli.some((ruolo) => canonicalRole(ruolo) === canonicalRole(c?.role))
        );

  return squadreDelleTessere(scelte, { assorbeTutte, doppioni });
};

/* =========================================================================
   LE DUE PRIMITIVE DEI CALENDARI — usate da CalendarClub e MyCalendar (fetta F4)
   ========================================================================= */

/**
 * LA SQUADRA DEL GIOCATORE — una sola, e dalla tessera indicata.
 *
 * ⚠️ UN PLAYER HA UNA E UNA SOLA SQUADRA: è l'invariante del club, e per
 * questo qui non si fa l'unione delle tessere ma si legge quella che il
 * chiamante nomina. L'elenco `teams` di una tessera PLAYER porta quindi un
 * nome solo: si prende il primo nome vero che contiene, senza inventare un
 * ripiego se l'elenco non c'è o è vuoto — chi non ha squadra non ha squadra.
 *
 * ⚠️ `allTeams` NON SI LEGGE (differenza D7): «tutte le squadre» è un
 * permesso da allenatore, e su una tessera non-COACH nessun lettore lo onora.
 * Con la regola B una tessera PLAYER storica che porti `approvedTeams:["ALL"]`
 * dà `{teams: [], allTeams: true}`, e questa funzione restituisce `""` — cioè
 * lo stesso esito di oggi. Nessun rischio di dare a un giocatore tutte le
 * squadre.
 *
 * Chiamata da `CalendarClub` e `MyCalendar` (ramo giocatore), dalla fetta F4.
 *
 * @returns {string} il nome, oppure "" se non ce n'è
 */
export const primaSquadraDellaTessera = (ctx) => {
  const { teams } = squadreDellaTessera(ctx);

  const nome = teams.find((voce) => normalizza(voce) !== "");

  return nome ? String(nome).trim() : "";
};

/**
 * «Questo evento è di una delle mie squadre?»
 *
 * L'appartenenza a un elenco prende il posto dell'uguaglianza a un nome solo:
 * è ciò che permette a un allenatore di U10 e U14 di vederle entrambe, e per
 * il giocatore — elenco di uno — resta identica all'uguaglianza di prima.
 *
 * Il confronto passa dal metro di casa (`normalizza`) sui DUE lati, lo stesso
 * che il backend applica ai nomi di squadra: "U12" e " u12 " sono la stessa
 * squadra. Un evento senza squadra non appartiene a nessun elenco.
 *
 * Chiamata da `CalendarClub` e `MyCalendar`, dalla fetta F4.
 * Corpo: quello delle DUE copie identiche di `CalendarClub` e `MyCalendar`.
 */
export const eDiUnaMiaSquadra = (nomeEvento, nomi) => {
  const chiave = normalizza(nomeEvento);
  if (chiave === "") return false;

  return nomi.some((nome) => normalizza(nome) === chiave);
};

/* =========================================================================
   LO STATO DELLA PERSONA PER UN ANNO — la regola di Team Assignment
   (cantiere «User List per anno guardato», YS-13 Punto 3)
   ========================================================================= */

/**
 * LO STATO DI UN INSIEME DI TESSERE GIÀ SCELTE — la «regola del caso raro»
 * di Team Assignment, nella forma in cui quella pagina la scriveva DUE volte
 * (la tendina Status e il colore di fila).
 *
 * ⚠️ È LA PRECEDENZA DI TEAM ASSIGNMENT, NON QUELLA DELLO SPECCHIO BACKEND.
 * Fra più tessere dello stesso anno in stati diversi vince lo stato «più
 * indietro», cioè «c'è ancora qualcosa da fare su questa persona»:
 *   almeno una PENDING      → PENDING
 *   altrimenti tutte ARCHIVED → ARCHIVED
 *   altrimenti              → ACTIVE
 * Lo specchio `status` della persona che il backend ricalcola segue la
 * precedenza OPPOSTA (ACTIVE > PENDING > ARCHIVED): è un'altra domanda —
 * «può entrare?» — e non va scritta qui. Questo modulo non scrive nulla.
 *
 * ⚠️ NESSUNA TESSERA → `ripiego`, che il chiamante passa ESPLICITO. Non c'è
 * un default perché dedurre PENDING dal vuoto mostrerebbe in attesa
 * d'approvazione tutti gli amministratori, le cui tessere non portano l'anno
 * (v. `eTesseraAmministrativa`); e inventare ACTIVE sarebbe altrettanto
 * arbitrario. Chi chiama sa da dove ripiegare (lo `status` della persona).
 *
 * ⚠️ UNA TESSERA CON STATO IGNOTO O ASSENTE conta come ACTIVE, ed è la
 * lettera della regola di Team Assignment (non PENDING, non tutte ARCHIVED →
 * ACTIVE): non si aggiunge qui un quarto esito che quella pagina non ha.
 *
 * `c?.status` e non `c.status`: il colore di fila di Team Assignment legge
 * `r.context?.status`, dove la «riga base» ha `context: null` e vale stato
 * vuoto — che non è ARCHIVED e tiene la fila neutra. Sulle tessere vere (mai
 * nulle, `tessereDelSoggetto` le scarta) i due accessi sono la stessa cosa.
 *
 * Corpo: quello di `statoMostrato` in TeamAssignment.jsx (RENEW-01,
 * intervento 3), invariato.
 */
export const statoDelleTessere = (tessere, ripiego) => {
  const stati = (Array.isArray(tessere) ? tessere : []).map((c) =>
    normalizza(c?.status)
  );

  return stati.length === 0
    ? ripiego
    : stati.includes("PENDING")
    ? "PENDING"
    : stati.every((s) => s === "ARCHIVED")
    ? "ARCHIVED"
    : "ACTIVE";
};

/**
 * LO STATO DI UNA PERSONA PER L'ANNO CHIESTO — «in che stato è questa
 * persona, guardando quest'annata?».
 *
 * Sono le tessere dell'anno IN QUALUNQUE STATO (`tessereDelSoggetto` con
 * `stati: null`, la lettura di Team Assignment, D1) passate alla regola qui
 * sopra. Con `stati` al default (solo ACTIVE) la domanda non avrebbe senso:
 * la risposta sarebbe ACTIVE per costruzione.
 *
 * `opzioni.ripiegoPersona`  lo stato da restituire quando la persona NON ha
 *                           tessere dell'anno — il caso degli amministratori.
 *                           Il chiamante passa lo `status` della persona,
 *                           normalizzato col SUO metro (Team Assignment
 *                           `normalizza(u.status)`, User List `statoDi(u)`).
 *                           Esplicito, senza default: v. `statoDelleTessere`.
 *
 * ⚠️ ANNATA IGNOTA (`anno === null`) → entrano le tessere di OGNI anno, in
 * ogni stato: è la dottrina di `tesseraDellAnno`, e la stessa lettura che
 * Team Assignment fa in quel caso.
 *
 * Chiamata da UsersList (base della tabella, base delle card, riga ambra).
 * Team Assignment non la chiama: usa `statoDelleTessere` — la regola
 * condivisa — sulle tessere dell'anno, che legge una volta sola (rifinitura
 * R4). Due pagine, una regola.
 */
export const statoDelSoggetto = (contexts, anno, opzioni = {}) => {
  const { ripiegoPersona } = opzioni;

  return statoDelleTessere(
    tessereDelSoggetto(contexts, anno, { stati: null }),
    ripiegoPersona
  );
};
